/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Build 93 — pre-loaded "MockFriend" example member that ships in every
// new user's SafeCircle until they dismiss it. NOT persisted to Firebase
// (nearme-79385) — it lives entirely client-side so production data
// stays clean. Lets a brand-new account see a populated orbit instead
// of an empty ring, so the SafeCircle feature is legible in 5 seconds.

import type { SafeCircleStaticMember } from "./staticMembers";

export const MOCK_FRIEND_ID = "mock-friend";

// Member status — drives the ring colour, the status dot under the
// avatar, and the "Current status" row on the detail screen. Real
// members are "unknown" until the Phase 2 presence + route layer ships;
// MockFriend is anchored at "driving" so the example shows a non-trivial
// status colour without being alarming.
export type MemberStatus =
  | "home"
  | "driving"
  | "sleeping"
  | "lowBattery"
  | "offline"
  | "unknown";

// View-model the SafeCircle screens consume. Wraps the persisted
// SafeCircleStaticMember with the richer fields the detail screen
// needs. Real members' status/city/batteryPct/isCharging/lastUpdatedAt
// stay undefined until Phase 2 — screens must render placeholders, not
// fabricate values.
export interface CircleMemberView {
  memberId: string;
  displayName: string;
  relation?: string;
  status: MemberStatus;
  city?: string;
  batteryPct?: number;
  isCharging?: boolean;
  lastUpdatedAt?: number;
  isMock: boolean;
  phoneNumber?: string;
  linkedFriendUid?: string;
  /** Inline SVG portrait used only by MockFriend. Real members fall
   *  through to the initial-on-color avatar via avatarColorForUsername. */
  avatarSvg?: string;
}

// Small inline cartoon portrait — matches the example avatar in the
// design mockup. Ships inline so there's no extra asset round-trip and
// no risk of a 404 in the WKWebView. The shape is intentionally
// gender-neutral / non-specific so it reads as "an example", not "a
// stand-in for a real person".
const MOCK_FRIEND_AVATAR_SVG = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'><defs><linearGradient id='bg' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%238b6df5'/><stop offset='1' stop-color='%236f4fe8'/></linearGradient></defs><rect width='120' height='120' fill='url(%23bg)'/><circle cx='60' cy='46' r='22' fill='%23f3eee6'/><path d='M22 108c0-21 17-34 38-34s38 13 38 34z' fill='%23f3eee6'/><path d='M38 40c0-16 12-24 22-24s22 8 22 24c0 4-2 7-4 9-1-9-8-14-18-14s-17 5-18 14c-2-2-4-5-4-9z' fill='%233a2f1f'/></svg>`;

// Frozen so screens that read this constant can't accidentally mutate
// shared state. Build 93 (post-feedback) — status / city / batteryPct /
// isCharging / lastUpdatedAt are now overlaid from the logged-in user's
// own live presence snapshot at hook-time (see useUserPresenceSnapshot
// + buildCircleView). The constants here are display defaults for the
// loading window before geolocation + Device.getBatteryInfo resolve.
export const MOCK_FRIEND_BASE: Readonly<CircleMemberView> = Object.freeze({
  memberId: MOCK_FRIEND_ID,
  displayName: "MockFriend",
  relation: "Brother",
  status: "unknown",
  city: undefined,
  batteryPct: undefined,
  isCharging: undefined,
  lastUpdatedAt: undefined,
  isMock: true,
  avatarSvg: MOCK_FRIEND_AVATAR_SVG,
});

// Per-render overlay populated by the SafeCircle hook from the logged-
// in user's live presence. Fields are independently optional so each
// piece (city, battery, etc.) can resolve at its own pace without
// holding the others up.
export interface UserPresenceOverlay {
  status?: MemberStatus;
  city?: string;
  batteryPct?: number;
  isCharging?: boolean;
  lastUpdatedAt?: number;
}

// Map a persisted member → view model. Phase 1 fields not yet sourced
// from a real signal stay undefined; the detail screen renders them as
// "Not shared yet" placeholders rather than invented values.
export function viewFromMember(m: SafeCircleStaticMember): CircleMemberView {
  return {
    memberId: m.memberId,
    displayName: m.displayName,
    relation: m.relation,
    status: "unknown",
    isMock: false,
    phoneNumber: m.phoneNumber,
    linkedFriendUid: m.linkedFriendUid,
  };
}

// Build the full ordered view list — MockFriend (when not dismissed) +
// the persisted members. Pure helper so it's unit-testable without
// React. `now` is the per-render epoch ms (callers in the app pass
// `Date.now()`; tests pass a constant). `userPresence` is the optional
// overlay built from useUserPresenceSnapshot — when present, its
// fields beat MOCK_FRIEND_BASE's loading defaults. Real (non-mock)
// members are never affected by userPresence.
export function buildCircleView(
  members: SafeCircleStaticMember[],
  mockFriendDismissed: boolean,
  now: number,
  userPresence?: UserPresenceOverlay,
): CircleMemberView[] {
  const list: CircleMemberView[] = [];
  if (!mockFriendDismissed) {
    const mock: CircleMemberView = {
      ...MOCK_FRIEND_BASE,
      status: userPresence?.status ?? MOCK_FRIEND_BASE.status,
      city: userPresence?.city ?? MOCK_FRIEND_BASE.city,
      batteryPct: userPresence?.batteryPct ?? MOCK_FRIEND_BASE.batteryPct,
      isCharging: userPresence?.isCharging ?? MOCK_FRIEND_BASE.isCharging,
      lastUpdatedAt:
        userPresence?.lastUpdatedAt ??
        // Loading-state fallback: show "Just now" via current `now` so
        // the relative-time row doesn't read "Not shared yet" the
        // instant the screen mounts.
        now,
    };
    list.push(mock);
  }
  for (const m of members) list.push(viewFromMember(m));
  return list;
}

// Count of real (non-mock) members, used by the MockFriend remove-rule:
// remove is disabled until the user has at least one real member.
export function realMemberCount(members: SafeCircleStaticMember[]): number {
  return members.length;
}
